import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Building2, DollarSign, TrendingUp, Plus, LogOut, Home, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { usePortfolioAuth } from '@/context/PortfolioAuthContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PortfolioDashboard() {
  const navigate = useNavigate();
  const { user, isAuthenticated, token, logout } = usePortfolioAuth();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [propertyToDelete, setPropertyToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    // Check authentication
    if (!isAuthenticated()) {
      navigate('/portfolio/login');
      return;
    }

    fetchProperties();
  }, [isAuthenticated, navigate]);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/portfolio/properties`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
      if (error.response?.status === 401) {
        logout();
        navigate('/portfolio/login');
      } else {
        toast.error('Failed to load properties');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/portfolio/login');
    toast.success('Logged out successfully');
  };

  const handleEditProperty = (e, propertyId) => {
    e.stopPropagation(); // Prevent card click navigation
    navigate(`/portfolio/edit-property/${propertyId}`);
  };

  const handleDeleteClick = (e, property) => {
    e.stopPropagation(); // Prevent card click navigation
    setPropertyToDelete(property);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!propertyToDelete) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/portfolio/properties/${propertyToDelete.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      toast.success('Property deleted successfully');
      // Remove property from local state
      setProperties(properties.filter(p => p.id !== propertyToDelete.id));
    } catch (error) {
      console.error('Error deleting property:', error);
      if (error.response?.status === 401) {
        logout();
        navigate('/portfolio/login');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to delete property');
      }
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setPropertyToDelete(null);
    }
  };

  const calculateTotals = () => {
    const totalValue = properties.reduce((sum, prop) => sum + (prop.purchase_price || 0), 0);
    const totalMonthlyRent = properties.reduce((sum, prop) => sum + (prop.monthly_rent || 0), 0);
    const avgRoi = properties.length > 0 ? 8.5 : 0; // Placeholder - would calculate from financials
    
    return { totalValue, totalMonthlyRent, avgRoi };
  };

  const { totalValue, totalMonthlyRent, avgRoi } = calculateTotals();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#1e3a8a]"></div>
          <p className="mt-4 text-slate-600">Loading portfolio...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center py-4 sm:py-6 space-y-4 sm:space-y-0">
            <div className="flex items-center space-x-3 sm:space-x-4 w-full sm:w-auto">
              <div className="h-10 w-10 sm:h-12 sm:w-12 bg-[#1e3a8a] rounded-full flex-shrink-0 flex items-center justify-center">
                <Building2 className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              </div>
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl font-bold text-[#1e3a8a] truncate">Portfolio Dashboard</h1>
                <p className="text-sm text-slate-600 truncate">Welcome back, {user?.name}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-3 w-full sm:w-auto justify-between sm:justify-end">
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="border-slate-300"
              >
                <Home className="mr-2 h-4 w-4" />
                Home
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-red-300 text-red-600 hover:bg-red-50"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="border-l-4 border-[#1e3a8a]">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Total Portfolio Value</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center space-x-2">
                <DollarSign className="h-5 w-5 text-[#1e3a8a]" />
                <p className="text-3xl font-bold text-[#1e3a8a]">
                  ${totalValue.toLocaleString()}
                </p>
              </div>
              <p className="text-xs text-slate-500 mt-2">{properties.length} properties</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-[#10b981]">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Total Monthly Cash Flow</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center space-x-2">
                <DollarSign className="h-5 w-5 text-[#10b981]" />
                <p className="text-3xl font-bold text-[#10b981]">
                  ${totalMonthlyRent.toLocaleString()}
                </p>
              </div>
              <p className="text-xs text-slate-500 mt-2">Monthly rent income</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-[#059669]">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Average ROI</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center space-x-2">
                <TrendingUp className="h-5 w-5 text-[#059669]" />
                <p className="text-3xl font-bold text-[#059669]">
                  {avgRoi.toFixed(1)}%
                </p>
              </div>
              <p className="text-xs text-slate-500 mt-2">Across all properties</p>
            </CardContent>
          </Card>
        </div>

        {/* Properties Section */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-[#1e3a8a]">My Properties</h2>
          <Button
            onClick={() => navigate('/portfolio/add-property')}
            className="bg-[#1e3a8a] hover:bg-[#1e3a8a]/90 text-white"
          >
            <Plus className="mr-2 h-5 w-5" />
            Add Property
          </Button>
        </div>

        {properties.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Building2 className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">No Properties Yet</h3>
              <p className="text-slate-600 mb-6">
                Get started by adding your first investment property
              </p>
              <Button
                onClick={() => navigate('/portfolio/add-property')}
                className="bg-[#1e3a8a] hover:bg-[#1e3a8a]/90 text-white"
              >
                <Plus className="mr-2 h-5 w-5" />
                Add Your First Property
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {properties.map((property) => (
              <Card
                key={property.id}
                className="hover:shadow-lg transition-shadow cursor-pointer border border-slate-200"
                onClick={() => navigate(`/portfolio/property/${property.id}`)}
              >
                <CardHeader className="bg-gradient-to-r from-[#1e40af] to-[#1e3a8a] text-white">
                  <CardTitle className="text-lg flex items-start justify-between">
                    <span className="flex-1 pr-2">{property.address}</span>
                    <Building2 className="h-5 w-5 flex-shrink-0" />
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-600">Purchase Price</span>
                      <span className="font-semibold text-slate-900">
                        ${(property.purchase_price || 0).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-600">Monthly Rent</span>
                      <span className="font-semibold text-[#10b981]">
                        ${(property.monthly_rent || 0).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-600">Down Payment</span>
                      <span className="font-semibold text-slate-900">
                        ${(property.down_payment || 0).toLocaleString()}
                      </span>
                    </div>
                    <div className="pt-3 border-t border-slate-200">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-slate-500">Property Type</span>
                        <span className="text-xs font-medium text-slate-700">
                          {property.property_type || 'N/A'}
                        </span>
                      </div>
                      {property.bedrooms > 0 && (
                        <div className="flex justify-between items-center mt-1">
                          <span className="text-xs text-slate-500">Beds/Baths</span>
                          <span className="text-xs font-medium text-slate-700">
                            {property.bedrooms} bd / {property.bathrooms} ba
                          </span>
                        </div>
                      )}
                    </div>
                    {/* Edit and Delete Buttons */}
                    <div className="pt-3 border-t border-slate-200 flex justify-end space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-[#1e3a8a] border-[#1e3a8a] hover:bg-[#1e3a8a] hover:text-white"
                        onClick={(e) => handleEditProperty(e, property.id)}
                      >
                        <Pencil className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 border-red-300 hover:bg-red-600 hover:text-white hover:border-red-600"
                        onClick={(e) => handleDeleteClick(e, property)}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white border border-slate-200 shadow-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-bold text-slate-900">Delete Property</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-slate-600">
                <p>Are you sure you want to delete this property?</p>
                {propertyToDelete && (
                  <p className="mt-2 font-semibold text-slate-800">
                    {propertyToDelete.address}
                  </p>
                )}
                <p className="mt-2 text-sm text-red-600">
                  This action cannot be undone.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-4">
            <AlertDialogCancel 
              disabled={deleting}
              className="border-slate-300 hover:bg-slate-100"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white border-0"
            >
              {deleting ? 'Deleting...' : 'Delete Property'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
